/**
 * useTextSelection — Detect text selections within a TipTap editor.
 *
 * Returns the selected text, character offsets (from/to within the doc),
 * and screen coordinates for positioning a tooltip.
 *
 * See: docs/features/document-system/document-comments.md (Phase 7)
 */
import { useState, useEffect, useCallback } from "react";

export function useTextSelection(editor) {
  const [selection, setSelection] = useState(null);

  const updateSelection = useCallback(() => {
    if (!editor) {
      setSelection(null);
      return;
    }

    const { state } = editor;
    const { from, to, empty } = state.selection;

    if (empty) {
      setSelection(null);
      return;
    }

    const text = state.doc.textBetween(from, to, " ");
    if (!text.trim()) {
      setSelection(null);
      return;
    }

    // Get screen coordinates from the DOM selection for tooltip positioning
    const domSelection = window.getSelection();
    let coords = null;
    if (domSelection && domSelection.rangeCount > 0) {
      const range = domSelection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      coords = {
        top: rect.top,
        left: rect.left + rect.width / 2,
        bottom: rect.bottom,
      };
    }

    setSelection({
      from,
      to,
      text,
      coords,
    });
  }, [editor]);

  useEffect(() => {
    if (!editor) return;

    editor.on("selectionUpdate", updateSelection);
    // Also listen for blur to clear selection
    editor.on("blur", () => setSelection(null));

    return () => {
      editor.off("selectionUpdate", updateSelection);
      editor.off("blur", () => setSelection(null));
    };
  }, [editor, updateSelection]);

  const clearSelection = useCallback(() => {
    setSelection(null);
  }, []);

  return { selection, clearSelection };
}
